import React, { useState } from "react";
import { Box, Typography, IconButton, Tooltip, Button, CircularProgress } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import DeleteIcon from "@mui/icons-material/Delete";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import { Handle, Position } from "@xyflow/react";
import { NodeControls } from "./nodeControls";

// Default fallback configuration if node data doesn't provide these values
const DEFAULT_CONFIG = {
  label: "Trigger",
  description: "Click to execute workflow",
  color: "#E53E3E",
  hoverColor: "#FF4D4D",
  showExecuteButton: true,
  executeButtonText: "Execute workflow",
};

export const DefaultTriggerNode = ({ id, data, highlighted, selected }) => {
  const onAddNext = data?.onAddNext;
  const [hovered, setHovered] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // Get configuration from node data with fallbacks to default
  const config = {
    label: data.label || DEFAULT_CONFIG.label,
    description: data.description || DEFAULT_CONFIG.description,
    color: data.color || DEFAULT_CONFIG.color,
    hoverColor: data.hoverColor || DEFAULT_CONFIG.hoverColor,
    showExecuteButton:
      data.showExecuteButton !== undefined
        ? data.showExecuteButton
        : DEFAULT_CONFIG.showExecuteButton,
    executeButtonText: data.executeButtonText || DEFAULT_CONFIG.executeButtonText,
  };

  // Determine current execution state
  const nodeStatus = data.status; // "running", "completed", "error", etc.
  const isNodeRunning = nodeStatus === "running";
  const isWorkflowExecuting = isExecuting;
  const isAnyExecuting = isNodeRunning || isWorkflowExecuting;

  const handleWorkflowExecution = async () => {
    if (!data?.onExecuteWorkflow || isAnyExecuting) {
      return;
    }

    // Set executing state with RAF to avoid layout thrashing
    requestAnimationFrame(() => {
      setIsExecuting(true);
    });

    // Add a small delay to ensure UI transitions complete smoothly
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      await data.onExecuteWorkflow(id);
    } catch (error) {
      console.error("DefaultTriggerNode: Workflow execution error:", error);
    } finally {
      // Add a small delay before finishing to avoid rapid state changes
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Use requestAnimationFrame to schedule state update during the next frame
      requestAnimationFrame(() => {
        setIsExecuting(false);
      });
    }
  };

  // Get display label from data or fall back to config
  const displayLabel = config.label;
  const displayDescription = config.description;

  // For schedule triggers, show schedule info if available
  const scheduleSettings = data.output?.settings;
  const scheduleDescription = scheduleSettings?.humanReadable;

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        "&:hover .node-controls": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Expanded hover buffer for smoother experience */}
      <Box
        sx={{
          position: "absolute",
          left: -200,
          top: "50%",
          height: 40,
          width: 200,
          transform: "translateY(-50%)",
          zIndex: 4,
        }}
      />

      {/* Execute button - shows for all trigger types with different text */}
      {config.showExecuteButton && (
        <Box
          sx={{
            position: "absolute",
            left: hovered ? -190 : -12,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 5,
            transition: "all 0.3s ease",
            height: 28,
            opacity: isAnyExecuting ? 0 : 1,
            visibility: isAnyExecuting ? "hidden" : "visible",
            pointerEvents: isAnyExecuting ? "none" : "auto",
          }}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<BoltIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isAnyExecuting) {
                handleWorkflowExecution();
              }
            }}
            sx={{
              backgroundColor: config.color,
              color: "white",
              height: 28,
              minWidth: hovered ? 160 : 28,
              borderRadius: "20px",
              padding: hovered ? "4px 12px 4px 8px" : "4px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              transition: "all 0.3s ease",
              boxShadow: hovered ? `0 0 8px 2px ${config.color}66` : "none",
              cursor: "pointer",
              "& .MuiButton-startIcon": {
                marginLeft: 0,
                marginRight: hovered ? "8px" : 0,
              },
              "&:hover": {
                backgroundColor: config.hoverColor,
              },
            }}
          >
            {hovered && config.executeButtonText}
          </Button>
        </Box>
      )}

      {/* Main Node Box */}
      <Box
        sx={{
          backgroundColor: "#414243",
          color: "#fff",
          borderRadius: 2,
          p: 2,
          paddingLeft: "25px",
          minWidth: 200,
          position: "relative",
          border: selected
            ? "2px solid #27BBD9"
            : isAnyExecuting
              ? "2px solid #FF6B35"
              : nodeStatus === "completed"
                ? "2px solid #4CAF50"
                : nodeStatus === "error"
                  ? "2px solid #F44336"
                  : "2px solid #C3C9D5",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          transition: "border 0.3s ease, box-shadow 0.3s ease",
          boxShadow: isAnyExecuting
            ? "0 0 12px rgba(255, 107, 53, 0.5)"
            : nodeStatus === "completed"
              ? "0 0 8px rgba(76, 175, 80, 0.3)"
              : nodeStatus === "error"
                ? "0 0 8px rgba(244, 67, 54, 0.3)"
                : "none",
          // Use CSS variables for animation to make it more efficient
          "--pulse-color": isAnyExecuting ? "rgba(255, 107, 53, 0.7)" : "transparent",
          ...(isAnyExecuting && {
            "&::after": {
              content: '""',
              position: "absolute",
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: "inherit",
              boxShadow: "0 0 15px var(--pulse-color)",
              animation: "staticPulse 2s infinite",
              pointerEvents: "none",
              zIndex: -1,
            },
          }),
          "@keyframes staticPulse": {
            "0%": {
              opacity: 0.5,
            },
            "50%": {
              opacity: 1,
            },
            "100%": {
              opacity: 0.5,
            },
          },
        }}
      >
        {/* Execution Status Badge - Always render but control visibility with opacity */}
        <Box
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: isAnyExecuting
              ? "#FF6B35"
              : nodeStatus === "completed"
                ? "#4CAF50"
                : "#F44336",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid white",
            zIndex: 20,
            opacity: isAnyExecuting || nodeStatus === "completed" || nodeStatus === "error" ? 1 : 0,
            transform:
              isAnyExecuting || nodeStatus === "completed" || nodeStatus === "error"
                ? "scale(1)"
                : "scale(0.8)",
            transition: "opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease",
            visibility:
              isAnyExecuting || nodeStatus === "completed" || nodeStatus === "error"
                ? "visible"
                : "hidden",
          }}
        >
          {isAnyExecuting ? (
            <BoltIcon sx={{ fontSize: 14, color: "white" }} />
          ) : nodeStatus === "completed" ? (
            <CheckCircleIcon sx={{ fontSize: 14, color: "white" }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 14, color: "white" }} />
          )}
        </Box>
        {/* Floating Controls */}
        <NodeControls
          nodeId={id}
          onRun={() => data.onRun?.(id)}
          onSettingsClick={() => data.onSettingsClick(id)}
          onDisable={() => data.onDisable?.(id)}
        />
        {/* Content */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {data.icon ? (
            <img
              src={require(`../../../../assets/${data.icon}.png`)}
              alt="Trigger Icon"
              style={{ height: "18px" }}
            />
          ) : (
            <BoltIcon sx={{ fontSize: 18, color: config.color }} />
          )}
          <Typography fontWeight="bold">{displayLabel}</Typography>
        </Box>
        <Typography fontSize={12} color="gray">
          {displayDescription}
        </Typography>
        {/* Show schedule info for schedule triggers */}
        {(data.type === "trigger.schedule" || data.category === "schedule") &&
          scheduleDescription && (
            <Typography fontSize={10} color="#90A4AE" sx={{ mt: 1, fontStyle: "italic" }}>
              {scheduleDescription}
            </Typography>
          )}
        {nodeStatus != "idle" && (
          <Box sx={{ mt: 1, height: 22, position: "relative" }}>
            {/* Executing status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: isAnyExecuting ? 1 : 0,
                transform: isAnyExecuting ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: isAnyExecuting ? "visible" : "hidden",
              }}
            >
              <CircularProgress size={14} sx={{ color: "#FF6B35" }} />
              <Typography fontSize={11} sx={{ color: "#FF6B35", fontWeight: "medium" }}>
                {isWorkflowExecuting ? "Executing workflow..." : "Running..."}
              </Typography>
            </Box>

            {/* Completed status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: nodeStatus === "completed" && !isAnyExecuting ? 1 : 0,
                transform:
                  nodeStatus === "completed" && !isAnyExecuting
                    ? "translateY(0)"
                    : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: nodeStatus === "completed" && !isAnyExecuting ? "visible" : "hidden",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 14, color: "#4CAF50" }} />
              <Typography fontSize={11} sx={{ color: "#4CAF50", fontWeight: "medium" }}>
                Completed
              </Typography>
            </Box>

            {/* Error status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: nodeStatus === "error" && !isAnyExecuting ? 1 : 0,
                transform:
                  nodeStatus === "error" && !isAnyExecuting ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: nodeStatus === "error" && !isAnyExecuting ? "visible" : "hidden",
              }}
            >
              <ErrorIcon sx={{ fontSize: 14, color: "#F44336" }} />
              <Typography fontSize={11} sx={{ color: "#F44336", fontWeight: "medium" }}>
                Error
              </Typography>
            </Box>
          </Box>
        )}
        {/* Handle for connecting to next nodes */}
        {data.outputs && data.outputs.length > 0 ? (
          data.outputs.map((output, index) => {
            return (
              <Handle
                key={output.name || index}
                type="source"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAddNext) onAddNext(id, output.name);
                }}
                position={Position.Right}
                id={output.name || `out-${index}`}
                style={{
                  backgroundColor: "#00C49F",
                  width: 28,
                  height: 28,
                  right: "-15px",
                  borderRadius: "50%",
                  border: "2px solid white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: "bold",
                  color: "white",
                  cursor: "pointer",
                  top: index === 0 ? "50%" : `${50 + index * 40}%`,
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
                title={`Connect ${output.name || "output"}`}
              >
                +
              </Handle>
            );
          })
        ) : (
          // Default output handle for backward compatibility
          <Handle
            type="source"
            onClick={(e) => {
              e.stopPropagation();
              if (onAddNext) onAddNext(id);
            }}
            position={Position.Right}
            id="out"
            style={{
              backgroundColor: "#00C49F",
              width: 28,
              height: 28,
              right: "-15px",
              borderRadius: "50%",
              border: "2px solid white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              fontWeight: "bold",
              color: "white",
              cursor: "pointer",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
            title="Drag to connect"
          >
            +
          </Handle>
        )}
      </Box>
    </Box>
  );
};

// Export aliases for backward compatibility
export const ManualyTriggerNode = DefaultTriggerNode;
export const TriggerNode = DefaultTriggerNode;
